import { useEffect, type RefObject } from "react";
import {
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
  type MotionValue,
} from "framer-motion";
import { useMediaQuery } from "./useMediaQuery";

/** Hauteur de l'ouverture au-delà de laquelle le défilement ne décale plus. */
const DRIFT_RANGE = 700;
const SPRING = { stiffness: 140, damping: 20, mass: 0.6 };

/** Position du pointeur dans un rectangle, ramenée à [-1, 1] sur chaque axe. */
export function pointerRatio(
  clientX: number,
  clientY: number,
  rect: { left: number; top: number; width: number; height: number },
): { x: number; y: number } {
  if (!rect.width || !rect.height) return { x: 0, y: 0 };
  const clamp = (n: number) => Math.max(-1, Math.min(1, n));
  return {
    x: clamp(((clientX - rect.left) / rect.width) * 2 - 1),
    y: clamp(((clientY - rect.top) / rect.height) * 2 - 1),
  };
}

/** Décalage d'une couche au défilement : `rate` px par 100 px, plafonné. */
export function scrollDrift(scrollY: number, rate: number): number {
  return (Math.min(Math.max(scrollY, 0), DRIFT_RANGE) * rate) / 100;
}

export type DossierLayer = { x: MotionValue<number>; y: MotionValue<number> };

/**
 * Une couche du dossier : `depth` = amplitude au pointeur (px, négative pour
 * une couche du fond), `rate` = glissement au défilement (px pour 100 px).
 */
function useLayer(
  px: MotionValue<number>,
  py: MotionValue<number>,
  scrollY: MotionValue<number>,
  depth: number,
  rate: number,
  still: boolean,
): DossierLayer {
  const x = useTransform(px, (v) => (still ? 0 : v * depth));
  const y = useTransform(() =>
    still ? 0 : py.get() * depth + scrollDrift(scrollY.get(), rate),
  );
  return { x, y };
}

/**
 * Parallaxe des fiches épinglées par le trombone : à la souris, chaque couche
 * se décale selon sa profondeur et le dossier s'incline à peine ; au
 * défilement, les couches glissent à des vitesses différentes. Au doigt, seul
 * le défilement joue ; sans mouvement, rien ne bouge.
 */
export function useDossierParallax(ref: RefObject<HTMLElement | null>) {
  const reduce = useReducedMotion() ?? false;
  const finePointer = useMediaQuery("(pointer: fine)");
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const px = useSpring(rawX, SPRING);
  const py = useSpring(rawY, SPRING);
  const { scrollY } = useScroll();

  useEffect(() => {
    const el = ref.current;
    if (!el || reduce || !finePointer) return;
    const move = (e: PointerEvent) => {
      const r = pointerRatio(e.clientX, e.clientY, el.getBoundingClientRect());
      rawX.set(r.x);
      rawY.set(r.y);
    };
    const leave = () => {
      rawX.set(0);
      rawY.set(0);
    };
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerleave", leave);
    return () => {
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerleave", leave);
    };
  }, [ref, reduce, finePointer, rawX, rawY]);

  const rotateX = useTransform(py, (v) => (reduce ? 0 : v * -2));
  const rotateY = useTransform(px, (v) => (reduce ? 0 : v * 2.5));

  return {
    tilt: { rotateX, rotateY },
    back: useLayer(px, py, scrollY, -9, 5, reduce),
    front: useLayer(px, py, scrollY, -5, 3, reduce),
    card: useLayer(px, py, scrollY, 2, 0, reduce),
    clip: useLayer(px, py, scrollY, 11, -4, reduce),
    mail: useLayer(px, py, scrollY, 3, 1, reduce),
  };
}
